import { useState } from 'react';
import { useSetExclusiveCreatorMutation } from '../../../../generated/graphql';
import { Price, StepKey, Step, UpdatedPrice, PriceError } from './types';
import {
  getSteps,
  getToastMessages,
  getVariablesFromPrice,
  validateInitialPrice,
  validatePriceUpdate,
} from './helpers';
import toast from 'react-hot-toast';
import _ from 'lodash';

type SetExclusiveProps = {
  influencerId: number;
  onClose: () => void;
  onConvertSuccess: (price: Price) => void;
  isExclusive: boolean;
  updatedPrice: UpdatedPrice;
  subscriptionMonthlyPrice: number | null;
  subscriptionAnnualPrice: number | null;
};

export type SelectedType = {
  monthly: boolean;
  annual: boolean;
};

type SetExclusive = {
  priceError: PriceError;
  isSubmitting: boolean;
  selectedType: SelectedType;
  selectType: (p: { key: keyof SelectedType; value: boolean }) => void;
} & Step;

export const useSetExclusive = ({
  influencerId,
  onClose,
  onConvertSuccess,
  isExclusive,
  updatedPrice,
  subscriptionMonthlyPrice,
  subscriptionAnnualPrice,
}: SetExclusiveProps): SetExclusive => {
  const [step, setStep] = useState<StepKey>(isExclusive ? 'update' : 'start');
  const [price, setPrice] = useState<Price>({
    monthly: undefined,
    annual: undefined,
  });
  const [selectedType, setSelectedType] = useState<SelectedType>({
    monthly: true,
    annual: false,
  });
  const [error, setError] = useState<PriceError>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [setExclusiveCreatorMutation] = useSetExclusiveCreatorMutation();

  const onSave = async () => {
    setIsSubmitting(true);
    const toasMessages = getToastMessages(isExclusive, updatedPrice);
    return setExclusiveCreatorMutation({
      variables: {
        data: {
          influencerId: influencerId,
          ...getVariablesFromPrice(price),
        },
      },
    })
      .then(() => {
        onConvertSuccess(price);
        toast.success(toasMessages.success);
        onClose();
      })
      .catch((err) => {
        toast.error(toasMessages.error);
        setError({ general: err.message });
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const proceed = () => setStep('input');

  const close = () => {
    onClose();
  };

  const save = () => {
    const errorMessages = isExclusive
      ? validatePriceUpdate({
          price,
          updatedPrice,
          subscriptionAnnualPrice,
          subscriptionMonthlyPrice,
        })
      : validateInitialPrice({ price, selectedType });
    if (!_.isEmpty(errorMessages)) {
      setError(errorMessages);
      return;
    }
    onSave();
  };

  const changePrice = ({
    key,
    value,
  }: {
    key: UpdatedPrice;
    value: number;
  }) => {
    setError({});
    if (key === 'monthly' && value > 99999) {
      setError((prev) => ({
        ...prev,
        monthly: 'Price should have max 5 digits',
      }));
    }
    if (key === 'annual' && value > 99999) {
      setError((prev) => ({
        ...prev,
        annual: 'Price should have max 5 digits',
      }));
    }
    setPrice((prev) => ({ ...prev, [key]: value }));
  };

  const selectType = ({
    key,
    value,
  }: {
    key: keyof SelectedType;
    value: boolean;
  }) => {
    setError({});
    if (!value) {
      setPrice((prev) => ({ ...prev, [key]: undefined }));
    }
    setSelectedType((prev) => ({ ...prev, [key]: value }));
  };

  const steps = getSteps({ save, close, proceed, changePrice, updatedPrice });
  return {
    ...steps[step],
    priceError: error,
    isSubmitting,
    selectedType,
    selectType,
  };
};
